import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import type { BlogPostDto } from '../../../shared/models/blog.models';
import { BlogsService } from '../../../shared/services/blogs.service';
import {
  AdminButtonComponent,
  AdminCardComponent,
  AdminConfirmDialogService,
  AdminEmptyStateComponent,
  AdminPageHeaderComponent,
  AdminToastService,
} from '../../../shared/admin';
import { PermissionService } from '../../permissions/permission.service';

@Component({
  selector: 'app-platform-blogs-list-page',
  standalone: true,
  imports: [DatePipe, AdminPageHeaderComponent, AdminCardComponent, AdminButtonComponent, AdminEmptyStateComponent],
  template: `
    <section>
      <app-admin-page-header title="Blogs">
        <ng-container actions>
          @if (showCreate()) {
            <app-admin-button variant="primary" type="button" (click)="createNew()" [disabled]="!canCreate()">New blog post</app-admin-button>
          }
        </ng-container>
      </app-admin-page-header>

      @if (loading()) {
        <p class="cg-form-help">Loading…</p>
      } @else {
        <div class="cg-admin-stack">
          @for (post of items(); track post._id) {
            <app-admin-card>
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div class="font-medium">{{ post.title || 'Untitled' }}</div>
                  <div class="cg-form-help mt-1">
                    <span class="uppercase">{{ post.status }}</span>
                    @if (post.slug) {
                      <span> · /blog/{{ post.slug }}</span>
                    }
                    @if (post.publishedAt) {
                      <span> · {{ post.publishedAt | date: 'mediumDate' }}</span>
                    }
                  </div>
                </div>
                <div class="flex flex-wrap gap-2">
                  @if (showEdit()) {
                    <app-admin-button variant="ghost" type="button" (click)="goEdit(post)" [disabled]="!canEdit()">
                      Edit
                    </app-admin-button>
                  }
                  @if (showDuplicate()) {
                    <app-admin-button variant="ghost" type="button" (click)="duplicate(post)" [disabled]="!canDuplicate()">Duplicate</app-admin-button>
                  }
                  @if (showPublish() && post.status === 'PUBLISHED') {
                    <app-admin-button variant="ghost" type="button" (click)="unpublish(post)" [disabled]="!canPublish()">Unpublish</app-admin-button>
                  }
                  @if (showDelete()) {
                    <app-admin-button variant="danger" type="button" (click)="remove(post)" [disabled]="!canDelete()">Delete</app-admin-button>
                  }
                </div>
              </div>
            </app-admin-card>
          }
          @if (items().length === 0) {
            <app-admin-empty-state icon="file-text" title="No blog posts yet" />
          }
        </div>
      }
    </section>
  `,
})
export class PlatformBlogsListPageComponent implements OnInit {
  private readonly blogs = inject(BlogsService);
  private readonly router = inject(Router);
  private readonly confirmDialog = inject(AdminConfirmDialogService);
  private readonly toast = inject(AdminToastService);
  private readonly permissions = inject(PermissionService);

  readonly showCreate = computed(() => this.permissions.canShow('website.blogs', 'create'));
  readonly canCreate = computed(() => this.permissions.canExecute('website.blogs', 'create'));
  readonly showEdit = computed(() => this.permissions.canShow('website.blogs', 'edit'));
  readonly canEdit = computed(() => this.permissions.canExecute('website.blogs', 'edit'));
  readonly showDuplicate = computed(() => this.permissions.canShow('website.blogs', 'duplicate'));
  readonly canDuplicate = computed(() => this.permissions.canExecute('website.blogs', 'duplicate'));
  readonly showPublish = computed(() => this.permissions.canShow('website.blogs', 'publish'));
  readonly canPublish = computed(() => this.permissions.canExecute('website.blogs', 'publish'));
  readonly showDelete = computed(() => this.permissions.canShow('website.blogs', 'delete'));
  readonly canDelete = computed(() => this.permissions.canExecute('website.blogs', 'delete'));

  readonly items = signal<BlogPostDto[]>([]);
  readonly loading = signal(true);

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.blogs.listPlatform({ limit: 100 }).subscribe({
      next: (data) => {
        this.items.set(data.items);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  createNew(): void {
    if (!this.canCreate()) return;
    void this.router.navigate(['/platform/blogs/new']);
  }

  goEdit(post: BlogPostDto): void {
    if (!this.canEdit()) return;
    void this.router.navigate(['/platform/blogs', post._id, 'edit']);
  }

  duplicate(post: BlogPostDto): void {
    if (!this.canDuplicate()) return;
    this.blogs.duplicate(post._id).subscribe({
      next: (copy) => {
        this.toast.success('Blog post duplicated.');
        this.router.navigate(['/platform/blogs', copy._id, 'edit']);
      },
      error: (err) => this.toast.error(err?.error?.message ?? 'Could not duplicate blog post.'),
    });
  }

  async unpublish(post: BlogPostDto): Promise<void> {
    const title = post.title?.trim() || 'Untitled';
    const confirmed = await this.confirmDialog.confirm({
      title: 'Unpublish blog post',
      message: `Unpublish "${title}"? It will no longer appear on the public website.`,
      confirmLabel: 'Unpublish',
      cancelLabel: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;

    this.blogs.unpublish(post._id).subscribe({
      next: () => {
        this.toast.success(`"${title}" unpublished.`);
        this.reload();
      },
      error: (err) => this.toast.error(err?.error?.message ?? 'Could not unpublish blog post.'),
    });
  }

  async remove(post: BlogPostDto): Promise<void> {
    const title = post.title?.trim() || 'Untitled';
    const confirmed = await this.confirmDialog.confirm({
      title: 'Delete blog post',
      message: `Delete "${title}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;

    this.blogs.removePlatform(post._id).subscribe({
      next: () => {
        this.toast.success(`"${title}" deleted.`);
        this.reload();
      },
      error: (err) => this.toast.error(err?.error?.message ?? 'Could not delete blog post.'),
    });
  }
}
